/**
 * iter_199 — My Premium Benefits card (owner-only).
 *
 * Renders directly under the profile header on the OWNER's profile.
 * Reads /api/me/tier for the authoritative feature list and shows one
 * green ✅ per unlocked feature. Locked features are HIDDEN from paid
 * members (per spec: "That way, artists instantly understand: Ohhh…
 * THIS is what I paid for.").
 *
 * Special modes:
 *   • Free tier            → cross-sell card with "See what Premium unlocks" CTA
 *   • Founding Artist      → gold Lifetime Founder card, no Manage / Billing controls
 *   • Premium (M or Y)     → benefits list + Manage Membership + Billing
 *   • Admin                → benefits list + admin badge, no billing
 *
 * Never rendered on someone else's profile — the small MembershipBadges
 * strip in the header handles the public case.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

// Ordered so the most user-facing perks show first.
const FEATURE_ORDER = [
  ["live_beam_create",      "🎙️", "Live Beams",           "Host live audio rooms and stream to fans"],
  ["contest_participate",   "🏆",  "Contest Entry",         "Enter every RGM contest, weekly and premium"],
  ["creator_hub_premium",   "🎨", "Creator Hub",           "AI cover art, mix polish, and mood tools"],
  ["beat_upload",           "🎧", "Beat Uploads",          "Sell beats and license instrumentals"],
  ["producer_mode",         "🎛️", "Producer Mode",         "Dual identity — artist and producer in one profile"],
  ["business_hub",          "💼", "Business Hub",          "List your services and get paid gigs"],
  ["business_listing_create","📇", "Business Listings",     "Publish paid gig listings clients can find"],
  ["ai_promo_tools",        "🤖", "AI Promotion",          "AI-generated promo tracks + auto-share captions"],
  ["premium_analytics",     "📊", "Advanced Analytics",    "Detailed listener + revenue insights"],
  ["earnings_dashboard",    "💰", "Earnings Dashboard",    "Track royalties, tips, and gift income"],
];

const TIER_META = {
  free: {
    label: "Free plan",
    icon: "🆓",
    subtitle: "You're on the Free plan.",
    chip: "bg-white/10 text-white border border-white/20",
    card: "bg-gradient-to-br from-purple-900/40 via-black/50 to-pink-900/25 border-purple-500/30",
  },
  premium_monthly: {
    label: "RGM Premium · Monthly",
    icon: "💎",
    subtitle: "Your unlocked features",
    chip: "bg-gradient-to-r from-cyan-400 to-blue-500 text-white",
    card: "bg-gradient-to-br from-cyan-700/25 via-purple-800/30 to-pink-800/20 border-cyan-400/40",
  },
  premium_yearly: {
    label: "RGM Premium · Yearly",
    icon: "💎",
    subtitle: "Your unlocked features",
    chip: "bg-gradient-to-r from-cyan-400 to-blue-500 text-white",
    card: "bg-gradient-to-br from-cyan-700/25 via-purple-800/30 to-pink-800/20 border-cyan-400/40",
  },
  founding_artist: {
    label: "Founding Artist",
    icon: "⭐",
    subtitle: "Lifetime access — every feature, forever",
    chip: "bg-gradient-to-r from-amber-400 via-pink-500 to-purple-600 text-white",
    card: "bg-gradient-to-br from-amber-500/25 via-purple-800/35 to-pink-800/25 border-amber-400/40",
  },
  admin: {
    label: "RGM Admin",
    icon: "⚡",
    subtitle: "Platform admin — full access",
    chip: "bg-purple-600 text-white",
    card: "bg-gradient-to-br from-purple-800/35 via-black/60 to-pink-800/25 border-purple-500/40",
  },
};

const PremiumBenefitsCard = ({ founderNumberLabel, foundingSinceLabel, isAdmin }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get("/me/tier")
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
        <Card className="p-5 bg-white/5 border-white/10 animate-pulse">
          <div className="h-5 w-32 bg-white/10 rounded mb-3" />
          <div className="h-4 w-3/4 bg-white/10 rounded mb-2" />
          <div className="h-4 w-1/2 bg-white/10 rounded" />
        </Card>
      </div>
    );
  }
  if (!data) return null;

  const tier = isAdmin ? "admin" : (data.tier || "free");
  const isFounder = !!founderNumberLabel;
  const effective = isFounder ? "founding_artist" : tier;
  const meta = TIER_META[effective] || TIER_META.free;

  // Unlocked features for this tier — from live /me/tier response.
  const unlocked = FEATURE_ORDER
    .filter(([key]) => data.features?.[key] === true)
    .map(([key, icon, name, desc]) => ({ key, icon, name, desc }));

  // Free-plan cross-sell view — show a compact "See what Premium unlocks"
  // card with a small preview of top 3 perks.
  if (effective === "free") {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
        <Card
          className={`${meta.card} p-5 sm:p-6 border relative overflow-hidden`}
          data-testid="profile-membership-section"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="min-w-0">
              <span
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${meta.chip}`}
                data-testid="profile-membership-tier-chip"
              >
                <span aria-hidden>{meta.icon}</span>
                <span data-testid="profile-membership-tier-label">{meta.label}</span>
              </span>
              <p className="mt-2.5 text-sm text-purple-200 max-w-xl" data-testid="profile-membership-benefit">
                Upgrade to unlock feature boosts, upload lifts, and priority visibility across RGM Studio.
              </p>
              <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[13px] text-gray-200">
                <li>🎙️ Live Beams</li>
                <li>🏆 Contest Entry</li>
                <li>🎧 Beat Uploads</li>
                <li>📊 Advanced Analytics</li>
              </ul>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:justify-end" data-testid="profile-membership-owner-actions">
              <Link to="/membership" className="w-full sm:w-auto">
                <Button
                  className="w-full sm:w-auto bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-bold shadow-lg shadow-purple-500/30"
                  data-testid="profile-membership-upgrade-btn"
                >
                  Upgrade Membership
                </Button>
              </Link>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // Founding Artist — lifetime, no billing controls.
  const showBillingControls = effective !== "founding_artist" && !isAdmin;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
      <Card
        className={`${meta.card} p-5 sm:p-6 border relative overflow-hidden`}
        data-testid="profile-membership-section"
      >
        {/* Soft accent orb */}
        <div className="pointer-events-none absolute -top-16 -right-16 w-56 h-56 rounded-full blur-3xl opacity-25 bg-white" aria-hidden />

        <div className="relative">
          {/* Header row */}
          <div className="flex flex-wrap items-center gap-2 justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${meta.chip}`}
                data-testid="profile-membership-tier-chip"
              >
                <span aria-hidden>{meta.icon}</span>
                <span data-testid="profile-membership-tier-label">{meta.label}</span>
              </span>
              {founderNumberLabel && (
                <span
                  className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold bg-gradient-to-r from-amber-400 to-pink-500 text-black"
                  data-testid="profile-founder-chip"
                >
                  ⭐ {founderNumberLabel}
                </span>
              )}
            </div>
            {showBillingControls && (
              <div className="flex flex-wrap items-center gap-2" data-testid="profile-membership-owner-actions">
                <Link to="/membership">
                  <Button
                    className="bg-white/10 border border-white/25 hover:bg-white/20 text-white font-bold"
                    data-testid="profile-membership-manage-btn"
                  >
                    Manage Membership
                  </Button>
                </Link>
                <Link to="/membership">
                  <Button
                    variant="ghost"
                    className="text-gray-200 hover:text-white hover:bg-white/10"
                    data-testid="profile-membership-billing-btn"
                  >
                    Billing
                  </Button>
                </Link>
              </div>
            )}
            {effective === "founding_artist" && (
              <div
                className="text-right"
                data-testid="profile-lifetime-founder-badge"
              >
                <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300/80 font-bold">
                  Permanent access
                </div>
                <div className="text-amber-100 text-sm font-bold">
                  Lifetime Founder
                </div>
              </div>
            )}
          </div>

          <p
            className="mt-3 text-[11px] uppercase tracking-[0.2em] text-purple-200/80 font-bold"
            data-testid="profile-membership-subtitle"
          >
            {meta.subtitle}
          </p>

          {/* Founder + admin heritage line */}
          {(foundingSinceLabel || (isAdmin && !isFounder)) && (
            <p className="text-amber-200/90 text-xs mt-1 font-medium" data-testid="profile-founder-label">
              {foundingSinceLabel || "RGM Studio team"}
            </p>
          )}

          {/* Unlocked benefits grid — only shown for paid/admin/founder. */}
          <ul
            className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2"
            data-testid="profile-benefits-list"
          >
            {unlocked.map(({ key, icon, name, desc }) => (
              <li
                key={key}
                className="flex items-start gap-2 text-sm"
                data-testid={`profile-benefit-${key}`}
              >
                <span className="text-emerald-400 font-bold flex-shrink-0" aria-hidden>✅</span>
                <span className="min-w-0">
                  <span className="text-white font-semibold">{icon} {name}</span>
                  <span className="block text-[11px] text-gray-400">{desc}</span>
                </span>
              </li>
            ))}
          </ul>

          {effective === "founding_artist" && (
            <p className="mt-3 text-[11px] text-amber-200/70" data-testid="profile-lifetime-note">
              You joined the Founding 100. Every feature above is permanently unlocked — no renewal, no billing.
            </p>
          )}
        </div>
      </Card>
    </div>
  );
};

export default PremiumBenefitsCard;
